import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  numberAttribute,
  output,
} from '@angular/core';

import { ControlValueAccessor, ReactiveFormsModule, ValidationErrors, Validator } from '@angular/forms';
import { inputProviders } from '@re-core/ngx-ui';
import { SafeHtml } from '@angular/platform-browser';
import { generateId } from '@lc/util';
import { noop } from 'rxjs';
import { BspkFormField } from '../form-field';
import { BspkTxt } from '../txt';
import { BspkTextarea, bspkTextareaDefaults, TextareaSize } from '../textarea';

/**
 * @see https://bspk.anywhere.re/textarea-field
 */
@Component({
  selector: 'bspk-textarea-field',
  imports: [BspkFormField, ReactiveFormsModule, BspkTxt, BspkTextarea],
  templateUrl: './textarea-field.component.html',
  styleUrl: './textarea-field.component.scss',
  providers: [...inputProviders(BspkTextareaField)],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BspkTextareaField implements Validator, ControlValueAccessor {
  readonly value = model('');
  readonly blur = output<Event>();
  readonly label = input.required<string>();
  readonly size = input<TextareaSize>(bspkTextareaDefaults.size);
  readonly maxLength = input<number | null>(null);
  readonly maxRows = input(bspkTextareaDefaults.maxRows, { transform: numberAttribute });
  readonly minRows = input(bspkTextareaDefaults.minRows, { transform: numberAttribute });
  readonly errorMessage = input<string | SafeHtml>();
  readonly helperText = input<string>();
  readonly required = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly readOnly = input(false, { transform: booleanAttribute });
  readonly invalid = input(false, { transform: booleanAttribute });
  readonly placeholder = input<string>('');
  readonly controlId = input(generateId('textarea'));
  readonly trailingLabel = computed(() => `${this.value().length}${this.maxLength() ? `/${this.maxLength()}` : ''}`);

  private onTouched: () => void = noop;
  private onChange: (value: any) => void = noop;

  protected handleChange(value: string) {
    this.value.set(value);
    this.onChange(value);
  }

  protected handleBlur(event: Event) {
    this.blur.emit(event);
    this.onTouched();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    // noop
  }

  writeValue(obj: any): void {
    this.value.set(obj);
  }

  registerOnValidatorChange(_fn: () => void): void {}

  validate(): ValidationErrors | null {
    let errors: ValidationErrors | null = null;

    const maxLength = this.maxLength();
    const value = this.value();
    if (maxLength && value.length > maxLength) {
      errors ??= {};
      errors['maxlength'] = true;
    }

    if (this.required() && value) {
      errors ??= {};
      errors['required'] = true;
    }

    return errors;
  }
}
